

import { GoogleGenAI, Part, Type } from "@google/genai";
import { FALLBACK_SAMPLE_PROMPTS } from '../constants';
import { apiService } from './apiService';

if (!process.env.API_KEY) {
  throw new Error("API_KEY environment variable not set.");
}

const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });

const fileToGenerativePart = (file: File): Promise<Part> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const base64Data = dataUrl.split(',')[1];
      if (!base64Data) {
        reject(new Error("Could not extract base64 data from file."));
        return;
      }
      resolve({
        inlineData: {
          data: base64Data,
          mimeType: file.type,
        },
      });
    };
    reader.onerror = error => reject(error);
  });
};


export const generateResponse = async (prompt: string, files: File[] = []): Promise<string> => {
  try {
    let contents: any;

    if (files && files.length > 0) {
      const fileParts = await Promise.all(files.map(fileToGenerativePart));
      const parts: Part[] = [{ text: prompt }];
      parts.push(...fileParts);
      contents = { parts };
    } else {
      contents = prompt;
    }

    const response = await ai.models.generateContent({
        model: 'gemini-2.5-flash',
        contents
    });
    return response.text;
  } catch (error) {
    if (error instanceof Error && error.message.toLowerCase().includes('quota')) {
        console.warn("Gemini API quota exceeded:", error);
        return "I'm experiencing high demand right now and need a short break. Please try again in a moment.";
    }
    console.error("Error generating response from Gemini API:", error);
    if (error instanceof Error) {
        return `Error: ${error.message}`;
    }
    return "An unknown error occurred while contacting the AI.";
  }
};

export const generateChatTitle = async (prompt: string): Promise<string> => {
  try {
    const titlePrompt = `Generate a short, concise title for a chat conversation that starts with the following message. The title should be no more than 5 words. Message: "${prompt}"`;
    const response = await ai.models.generateContent({
        model: 'gemini-2.5-flash',
        contents: titlePrompt,
    });
    // Remove quotes, asterisks, and other markdown characters, then trim.
    return response.text.replace(/["'*_]/g, "").trim();
  } catch (error) {
    if (error instanceof Error && error.message.toLowerCase().includes('quota')) {
        console.warn("Gemini API quota exceeded while generating title:", error);
    } else {
        console.error("Error generating chat title from Gemini API:", error);
    }
    // Fallback to using the first few words of the prompt as a title
    return prompt.split(' ').slice(0, 5).join(' ') + (prompt.split(' ').length > 5 ? '...' : '');
  }
};

export const generateSamplePrompts = async (userId?: string): Promise<{ title: string; prompt: string; }[]> => {
  const titles = FALLBACK_SAMPLE_PROMPTS.map(p => p.title);
  
  try {
    // Try to get suggestions from FastAPI first (with user_id for personalized suggestions if logged in)
    const response = await apiService.getAISuggestions('home', userId);
    
    if (response.success && response.data && response.data.suggestions && response.data.suggestions.length >= 3) {
      // Map the API suggestions to the expected format
      return titles.map((title, index) => ({
        title,
        prompt: response.data.suggestions[index] || FALLBACK_SAMPLE_PROMPTS[index].prompt,
      }));
    } else {
      console.warn("FastAPI suggestions not available, falling back to Gemini");
      // Fallback to Gemini if FastAPI fails
      return generateSamplePromptsWithGemini();
    }
  } catch (error) {
    console.warn("FastAPI AI suggestions failed, falling back to Gemini:", error);
    // Fallback to Gemini on network error
    return generateSamplePromptsWithGemini();
  }
};

// Gemini fallback function
const generateSamplePromptsWithGemini = async (): Promise<{ title: string; prompt: string; }[]> => {
  const titles = FALLBACK_SAMPLE_PROMPTS.map(p => p.title);
  
  try {
    const response = await ai.models.generateContent({
      model: 'gemini-2.5-flash',
      contents: `You are an AI assistant helping investors and researchers find university innovations. Generate 3 diverse example questions a user might ask, one for each of the following categories. Each question must be a single, clear sentence and should not exceed 150 characters. Return the result as a JSON array of strings, with each string being a prompt.
      
      Categories:
      1. "${titles[0]}"
      2. "${titles[1]}"
      3. "${titles[2]}"`,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.ARRAY,
          items: {
            type: Type.STRING,
            description: 'The example question a user might ask, under 150 characters.',
          },
        },
      },
    });

    const jsonString = response.text.trim();
    const prompts = JSON.parse(jsonString);
    
    if (Array.isArray(prompts) && prompts.length === 3 && typeof prompts[0] === 'string') {
      return titles.map((title, index) => ({
        title,
        prompt: prompts[index],
      }));
    } else {
      console.warn("Generated sample prompts are not in the expected format, using fallback.");
      return FALLBACK_SAMPLE_PROMPTS;
    }
  } catch (error) {
    if (error instanceof Error && error.message.toLowerCase().includes('quota')) {
        console.warn("Gemini API quota exceeded while generating sample prompts:", error);
    } else {
        console.error("Error generating sample prompts from Gemini API:", error);
    }
    return FALLBACK_SAMPLE_PROMPTS;
  }
};